// Lister-Detail-Tab "Varianten" — Tabelle aller Varianten (Vater + Kinder) eines
// Variantenvaters mit Status-Balken "X/Y Felder gemappt" (Pflichtluecke rot),
// Bild-Haken und Buttons fuer Mapping/Optionale Felder/Bilder/Vorschau pro Zeile.
// (Beim Variantenvater liegen diese vier Tabs PRO Zeile, nicht im Detail-Menue —
// das Menue zeigt dort nur Übersicht + Varianten.)
//
// Backend: kaufland-listing-preview action=variant-check (aggregiert pro Artikel).
// Buttons oeffnen die bestehenden renderMappingTab/renderOptionalFieldsTab/
// renderImagesTab/renderPreviewTab in einem Overlay — keine UI-Duplikation.

import type { SupabaseClient } from '@supabase/supabase-js';
import { renderMappingTab } from './kaufland-list-mapping.js';
import { renderImagesTab } from './kaufland-list-images.js';
import { renderPreviewTab } from './kaufland-list-preview.js';
import { renderOptionalFieldsTab } from './optional-fields.js';
import { showToast } from '@vario-connect/frontend-design';

export interface VariantsTabCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}
export interface VariantsTabListing {
  storefront: string;
  kaufland_category_id: number | null;
  vario_category_id: string | null;
  vario_listing_id: string;
  vario_article_id: string;
}

interface VariantCheckRow {
  articleId: string;
  listingId: string | null;
  isParent: boolean;
  combinationLabel: string;
  filledCount: number;
  totalCount: number;
  requiredGap: boolean;
  requiredMissing: string[];
  hasImage: boolean;
}

const STOREFRONT_LOCALES: Record<string, string> = {
  de: 'de-DE', at: 'de-AT', cz: 'cs-CZ', sk: 'sk-SK', pl: 'pl-PL', fr: 'fr-FR', it: 'it-IT',
};
function localeFor(sf: string): string { return STOREFRONT_LOCALES[sf] ?? 'de-DE'; }

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Sentinel-Tokens aus dem Backend in lesbaren Text uebersetzen — nie roh anzeigen.
function prettyMissing(keys: string[]): string {
  return keys
    .map((k) => {
      if (k === '__error__') return 'Fehler beim Laden';
      if (k === '__article_not_loaded__') return 'Artikel nicht ladbar';
      return k;
    })
    .join(', ');
}

async function callFn<T>(ctx: VariantsTabCtx, name: string, body: unknown): Promise<{ ok: true; value: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${name}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
    if (!res.ok) {
      const eb = (parsed as { error?: { code?: string; message?: string } } | null)?.error;
      return { ok: false, error: eb ? `${eb.code ?? '?'}: ${eb.message ?? '?'}` : `HTTP ${res.status}` };
    }
    return { ok: true, value: parsed as T };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

export async function renderVariantsTab(
  host: HTMLElement,
  ctx: VariantsTabCtx,
  listing: VariantsTabListing,
): Promise<void> {
  host.innerHTML = `<div class="klv-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Varianten …</div>`;

  if (!listing.kaufland_category_id) {
    host.innerHTML = `<div class="klv-empty">Dieses Listing hat noch keine Kaufland-Kategorie. Bitte zuerst im Vario-Slot zuweisen.</div>`;
    return;
  }

  const { data: profileResp, error: profileErr } = await ctx.supabase.rpc('kaufland_get_mapping_profile', {
    _storefront: listing.storefront, _locale: localeFor(listing.storefront), _category_id: listing.kaufland_category_id,
  });
  if (profileErr) { host.innerHTML = `<div class="klv-empty klv-empty--error">Profil-Ladung fehlgeschlagen: ${escapeHtml(profileErr.message)}</div>`; return; }
  const profile = (profileResp as { profile?: { id: string } | null } | null)?.profile ?? null;
  if (!profile) {
    host.innerHTML = `<div class="klv-empty">Noch kein Mapping-Profil gespeichert. Wechsle in den „Mapping"-Tab, speichere die Field-Mappings und komm dann hierher zurück.</div>`;
    return;
  }

  const resp = await callFn<{ rows: VariantCheckRow[] }>(ctx, 'kaufland-listing-preview', {
    action: 'variant-check',
    profileId: profile.id,
    articleId: listing.vario_article_id,
    listingId: listing.vario_listing_id,
  });
  if (!resp.ok) { host.innerHTML = `<div class="klv-empty klv-empty--error">Varianten-Check fehlgeschlagen: ${escapeHtml(resp.error)}</div>`; return; }

  // Vario-Base-URL fail-soft laden — wenn nicht verfuegbar bleibt das Link-Icon einfach weg.
  const { data: baseUrlData } = await ctx.supabase.rpc('kaufland_get_vario_base_url');
  const varioBaseUrl = typeof baseUrlData === 'string' && baseUrlData.length > 0 ? baseUrlData : null;

  renderTable(host, ctx, listing, resp.value.rows, varioBaseUrl);
}

/** Baut die Vario-UI-URL fuer eine Varianten-Zeile.
 *  Mit Listing-ID → Listing-Detail (Verkaufskanal-spezifisch).
 *  Ohne Listing-ID → Article-Detail (allgemein). */
function varioUrlForRow(baseUrl: string, articleId: string, listingId: string | null): string {
  const aid = encodeURIComponent(articleId);
  if (listingId) {
    return `${baseUrl}/products/articles/${aid}/listings/${encodeURIComponent(listingId)}`;
  }
  return `${baseUrl}/products/articles/${aid}/detail`;
}

function renderTable(host: HTMLElement, ctx: VariantsTabCtx, listing: VariantsTabListing, rows: VariantCheckRow[], varioBaseUrl: string | null): void {
  // "X / Y listbar" zaehlt NUR die Varianten — der Vater ist kein Kaufland-Produkt
  // (jede Variante ist eins, mit eigener EAN) und wird nie gepusht. Frueher lief er
  // mit in den Zaehler und verfaelschte ihn (bzw. haing wegen fehlender Vater-EAN
  // dauerhaft als "nicht listbar" drin).
  const children = rows.filter((r) => !r.isParent);
  const listable = children.filter((r) => !r.requiredGap && r.listingId).length;
  const missingChildren = rows.filter((r) => !r.isParent && !r.listingId);
  const createBlock = missingChildren.length > 0 ? `
    <div class="klv-create">
      <span class="klv-create-label">Varianten anlegen:</span>
      <label><input type="checkbox" class="klv-inh" data-inh="description" checked> Beschreibung</label>
      <label><input type="checkbox" class="klv-inh" data-inh="price" checked> Preis</label>
      <label><input type="checkbox" class="klv-inh" data-inh="optional_fields" checked> optionale Felder</label>
      <label><input type="checkbox" class="klv-inh" data-inh="name" checked> Name</label>
      <button type="button" class="v-button v-button--primary v-button--sm klv-create-all">Alle fehlenden anlegen (${missingChildren.length})</button>
    </div>` : '';

  const rowsHtml = rows.map((r) => {
    const pct = r.totalCount > 0 ? Math.round((r.filledCount / r.totalCount) * 100) : 0;
    const barCls = r.requiredGap ? 'klv-bar--gap' : 'klv-bar--ok';
    const name = r.isParent
      ? '★ Variantenvater <span class="klv-parent-note">(wird nicht gelistet)</span>'
      : escapeHtml(r.combinationLabel || r.articleId);
    const missingTitle = r.requiredMissing.length ? ` title="Fehlende Pflichtfelder: ${escapeHtml(prettyMissing(r.requiredMissing))}"` : '';
    const gapMark = r.requiredGap ? ' <span class="klv-gap-mark">(!)</span>' : '';
    const isMissingChild = !r.isParent && !r.listingId;

    const progressCell = isMissingChild
      ? `<td class="klv-progress"><span class="v-text-muted">— kein Listing</span></td>`
      : `<td class="klv-progress"${missingTitle}>
          <div class="klv-bar"><div class="klv-bar-fill ${barCls}" style="width:${pct}%"></div></div>
          <span class="klv-count ${r.requiredGap ? 'klv-count--gap' : ''}">${r.filledCount}/${r.totalCount}${gapMark}</span>
        </td>`;

    const imgCell = isMissingChild
      ? `<td class="klv-img">—</td>`
      : `<td class="klv-img">${r.hasImage ? '<span class="v-icon fa-solid fa-circle-check klv-img-ok"></span>' : '<span class="v-icon fa-regular fa-circle klv-img-missing"></span>'}</td>`;

    // Push-Vorschau nur fuer Varianten: der Vater wird nicht gepusht, seine "Vorschau
    // Listing-Push" zeigte nur eine Payload, die es nie gibt (samt irrefuehrendem
    // "EAN ist Pflicht"). Mapping/Optionale Felder/Bilder bleiben am Vater — das
    // Mapping-Profil ist geteilt und wird typischerweise hier gepflegt.
    const actionsCell = isMissingChild
      ? `<td class="klv-actions"><button type="button" class="v-button v-button--sm klv-create-one">Anlegen</button></td>`
      : `<td class="klv-actions">
          <button type="button" class="v-button v-button--sm" data-act="mapping">Mapping</button>
          <button type="button" class="v-button v-button--sm" data-act="optional">Optionale Felder</button>
          <button type="button" class="v-button v-button--sm" data-act="images">Bilder</button>
          ${r.isParent ? '' : '<button type="button" class="v-button v-button--sm" data-act="preview">Vorschau</button>'}
        </td>`;

    const varioLink = varioBaseUrl
      ? ` <a href="${escapeHtml(varioUrlForRow(varioBaseUrl, r.articleId, r.listingId))}" target="_blank" rel="noopener noreferrer" class="klv-vario-link" title="${r.listingId ? 'Listing in Vario öffnen' : 'Artikel in Vario öffnen'}"><span class="v-icon fa-regular fa-arrow-up-right-from-square"></span></a>`
      : '';

    return `
      <tr data-article-id="${escapeHtml(r.articleId)}" data-listing-id="${escapeHtml(r.listingId ?? '')}">
        <td class="klv-name"${r.isParent ? ' title="Der Variantenvater ist bei Kaufland kein eigenes Produkt und wird nicht gepusht — gelistet werden die Varianten, jede mit eigener EAN. Das Mapping-Profil gilt für alle."' : ''}>${name}${varioLink}</td>
        ${progressCell}
        ${imgCell}
        ${actionsCell}
      </tr>`;
  }).join('');

  host.innerHTML = `
    <div class="klv-shell">
      <div class="klv-header">Varianten · <strong>${listable} / ${children.length}</strong> listbar</div>
      ${createBlock}
      <table class="klv-table">
        <thead><tr><th>Artikel</th><th>Felder gemappt</th><th>Bild</th><th>Aktionen</th></tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="klv-overlay" hidden><div class="klv-overlay-panel"><button type="button" class="klv-overlay-close v-button v-button--sm">✕ Schließen</button><div class="klv-overlay-body"></div></div></div>
    </div>`;

  wireActions(host, ctx, listing, rows);
  wireCreate(host, ctx, listing, rows);
}

function wireActions(host: HTMLElement, ctx: VariantsTabCtx, listing: VariantsTabListing, _rows: VariantCheckRow[]): void {
  const overlay = host.querySelector<HTMLElement>('.klv-overlay')!;
  const overlayBody = host.querySelector<HTMLElement>('.klv-overlay-body')!;
  const closeBtn = host.querySelector<HTMLButtonElement>('.klv-overlay-close')!;
  const openOverlay = () => { overlay.hidden = false; };
  const closeOverlay = () => { overlay.hidden = true; overlayBody.innerHTML = ''; };
  closeBtn.addEventListener('click', closeOverlay);

  const subCtx = { supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token } };

  async function openSubTab(act: string, articleId: string, rowListingId: string): Promise<void> {
    overlayBody.innerHTML = `<div class="klv-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade …</div>`;
    openOverlay();
    try {
      if (act === 'mapping') {
        await renderMappingTab(overlayBody, subCtx, {
          storefront: listing.storefront,
          vario_category_id: listing.vario_category_id,
          kaufland_category_id: listing.kaufland_category_id,
          vario_listing_id: rowListingId,
          vario_article_id: articleId,
        });
      } else if (act === 'optional') {
        await renderOptionalFieldsTab(overlayBody, subCtx, {
          storefront: listing.storefront,
          vario_article_id: articleId,
          kaufland_category_id: listing.kaufland_category_id,
        });
      } else if (act === 'images') {
        await renderImagesTab(overlayBody, subCtx, { vario_article_id: articleId });
      } else if (act === 'preview') {
        await renderPreviewTab(overlayBody, subCtx, {
          storefront: listing.storefront,
          kaufland_category_id: listing.kaufland_category_id,
          vario_listing_id: rowListingId,
          vario_article_id: articleId,
        });
      }
    } catch (e) {
      // Sonst bleibt das Overlay leer ohne Hinweis (fire-and-forget schluckt den Fehler).
      console.error('[variants] sub-tab render failed', act, articleId, e);
      overlayBody.innerHTML = `<div class="klv-empty klv-empty--error">Konnte „${escapeHtml(act)}" nicht laden: ${escapeHtml((e as Error).message ?? String(e))}</div>`;
    }
  }

  host.querySelectorAll<HTMLButtonElement>('button[data-act]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tr = btn.closest('tr');
      const articleId = tr?.getAttribute('data-article-id') ?? '';
      const listingIdAttr = tr?.getAttribute('data-listing-id') ?? '';
      const rowListingId = listingIdAttr || listing.vario_listing_id;
      const act = btn.dataset['act'];
      if (!act) return;
      void openSubTab(act, articleId, rowListingId);
    });
  });
}

function wireCreate(host: HTMLElement, ctx: VariantsTabCtx, listing: VariantsTabListing, rows: VariantCheckRow[]): void {
  function readInherit(): { description: boolean; price: boolean; optional_fields: boolean; name: boolean } {
    const out = { description: false, price: false, optional_fields: false, name: false };
    host.querySelectorAll<HTMLInputElement>('.klv-inh').forEach((cb) => {
      const key = cb.dataset['inh'];
      if (key === 'description' || key === 'price' || key === 'optional_fields' || key === 'name') {
        out[key] = cb.checked;
      }
    });
    return out;
  }

  async function enqueueCreate(childIds: string[]): Promise<void> {
    if (childIds.length === 0) return;
    const inherit = readInherit();
    showToast(`Lege ${childIds.length} Variante(n) an …`, 'info');
    // Synchron: warten bis Vario die Listings angelegt + Inbox-Rows geschrieben hat,
    // dann sofort den Tab neu rendern. Edge-Function-Timeout ~150s.
    const resp = await callFn<{
      rows: Array<{ articleId: string; status: 'created' | 'skipped' | 'failed'; reason?: string }>;
      summary: { created: string[]; skipped: string[]; failed: Array<{ articleId: string; reason: string }>; total: number };
    }>(ctx, 'kaufland-create-variant-listings', {
      parent_article_id: listing.vario_article_id,
      parent_listing_id: listing.vario_listing_id,
      storefront: listing.storefront,
      vario_category_id: listing.vario_category_id,
      kaufland_category_id: listing.kaufland_category_id,
      child_article_ids: childIds,
      inherit,
    });
    if (!resp.ok) {
      showToast(`Anlage fehlgeschlagen: ${resp.error}`, 'error');
      return;
    }
    const s = resp.value.summary;
    const parts: string[] = [];
    if (s.created.length) parts.push(`${s.created.length} angelegt`);
    if (s.skipped.length) parts.push(`${s.skipped.length} bereits vorhanden`);
    if (s.failed.length) parts.push(`${s.failed.length} fehlgeschlagen`);
    const kind = s.failed.length > 0 ? 'error' : 'success';
    showToast(parts.join(' · ') || 'Anlage durch', kind);
    if (s.failed.length > 0) {
      console.warn('[variants] some children failed', resp.value.summary.failed);
    }
    if (host.isConnected) await renderVariantsTab(host, ctx, listing);
  }

  host.querySelector<HTMLButtonElement>('.klv-create-all')?.addEventListener('click', () => {
    void enqueueCreate(rows.filter((r) => !r.isParent && !r.listingId).map((r) => r.articleId));
  });
  host.querySelectorAll<HTMLButtonElement>('.klv-create-one').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tr = btn.closest('tr');
      const articleId = tr?.getAttribute('data-article-id') ?? '';
      if (articleId) void enqueueCreate([articleId]);
    });
  });
}
